import { useState } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useTemplates, useDeleteTemplate } from '@/hooks/useTemplates';
import { TemplateModal } from '@/components/modals/TemplateModal';
import { TemplateResponse } from '@/types/api';

export default function Templates() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<TemplateResponse | null>(null);

  const { data: templates, isLoading, error } = useTemplates();
  const deleteTemplate = useDeleteTemplate();

  const handleEdit = (template: TemplateResponse) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir este template?')) {
      deleteTemplate.mutate(id);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  if (error) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-bold mb-4">Templates</h1>
        <div className="text-red-500">Erro ao carregar templates: {error.message}</div>
      </div>
    );
  }

  if (isLoading) {
    return <div className="p-6">Carregando templates...</div>;
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Templates de Mensagem</h1>
        <Button onClick={() => setIsModalOpen(true)}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Template
        </Button>
      </div>

      <div className="grid gap-4">
        {templates && templates.length > 0 ? (
          templates.map((template) => (
            <Card key={template.id}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-lg">{template.name}</CardTitle>
                {template.variables && template.variables.length > 0 && (
                  <Badge variant="secondary">
                    {template.variables.length} variáveis
                  </Badge>
                )}
              </CardHeader>
              <CardContent>
                <div className="flex justify-between items-start">
                  <div className="space-y-2 flex-1">
                    <p className="text-sm text-muted-foreground line-clamp-3">
                      {template.content}
                    </p>
                    {template.variables && template.variables.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {template.variables.map((variable) => (
                          <Badge key={variable} variant="outline" className="text-xs">
                            {variable}
                          </Badge>
                        ))}
                      </div>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Criado em {new Date(template.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(template)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(template.id)}
                      disabled={deleteTemplate.isPending}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        ) : (
          <div className="text-center py-8 text-gray-500">
            Nenhum template encontrado
          </div>
        )}
      </div>

      <TemplateModal
        isOpen={isModalOpen}
        onClose={closeModal}
        template={editingTemplate}
      />
    </div>
  );
}